import { useEffect, useState } from 'react'
import { Info } from 'lucide-react'
import { reportsApi } from '../../../services/api'
import DateRangePicker from '../../../components/DateRangePicker'
import { CATEGORY_LABELS } from './ExpensesTab'
import { useSortableData } from '../../../hooks/useSortableData'
import SortableTh from '../../../components/SortableTh'

interface ExpenseByCategory {
  category: string
  total: number
}

interface CashPositionReport {
  cash_out: {
    total: number
    by_category: ExpenseByCategory[]
  }
  cash_in: {
    total_pending: number
    total_overdue: number
    collected_in_range: number
  }
  net: number
}

/**
 * Directional cash position (collected − spent), not a bank reconciliation
 * or double-entry ledger -- see plan for scope. "Cobrado" is explicitly
 * flagged as an approximation because it is derived from `sales.payment_date`
 * (a single-pass field), not a dedicated payment-events table.
 */
export default function CashPositionTab() {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [report, setReport] = useState<CashPositionReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError('')
    reportsApi
      .getCashPosition(startDate || undefined, endDate || undefined)
      .then((res) => {
        if (cancelled) return
        setReport(res.data)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Error fetching cash position report:', err)
        setLoadError('No se pudo cargar la posición de caja. Intenta recargar la página.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [startDate, endDate])

  const { sortedData: sortedByCategory, sortConfig, requestSort } = useSortableData(report?.cash_out.by_category || [])

  return (
    <div>
      <div className="bg-cream-50 shadow rounded-lg p-4 mb-6">
        <DateRangePicker
          idPrefix="cash-position-range"
          label="Rango de fechas:"
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
      </div>

      {loading ? (
        <div className="text-center py-8">Cargando...</div>
      ) : loadError ? (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm" role="alert">
          {loadError}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="flex items-center gap-1 text-sm font-medium text-espresso-500">
                Cobrado
                <span title="Aproximado: se calcula a partir de la fecha de pago registrada en cada venta, no de un registro de eventos de pago.">
                  <Info className="h-3.5 w-3.5 text-espresso-400" aria-hidden="true" />
                </span>
              </div>
              <div className="mt-2 text-2xl font-bold text-green-700">
                ${(report?.cash_in.collected_in_range || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="text-sm font-medium text-espresso-500">Gastado</div>
              <div className="mt-2 text-2xl font-bold text-red-600">
                ${(report?.cash_out.total || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-cream-50 shadow rounded-lg p-4">
              <div className="text-sm font-medium text-espresso-500">Neto</div>
              <div
                className={`mt-2 text-2xl font-bold ${
                  (report?.net || 0) >= 0 ? 'text-espresso-800' : 'text-red-600'
                }`}
              >
                ${(report?.net || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <p className="text-xs text-espresso-500 mb-6 flex items-center gap-1">
            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            El monto "Cobrado" es una aproximación basada en la fecha de pago de las ventas, no un
            registro auditado de eventos de pago. Cartera pendiente total: $
            {(report?.cash_in.total_pending || 0).toFixed(2)}, de la cual $
            {(report?.cash_in.total_overdue || 0).toFixed(2)} está vencida.
          </p>

          <h2 className="text-lg font-medium text-espresso-800 mb-3">Gastos por categoría</h2>
          {!report || report.cash_out.by_category.length === 0 ? (
            <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
              <p className="text-espresso-500">No hay gastos registrados en el rango seleccionado.</p>
            </div>
          ) : (
            <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-espresso-100">
                  <thead className="bg-cream-50">
                    <tr>
                      <SortableTh label="Categoría" sortKey="category" sortConfig={sortConfig} onSort={requestSort} />
                      <SortableTh label="Total gastado" sortKey="total" sortConfig={sortConfig} onSort={requestSort} />
                    </tr>
                  </thead>
                  <tbody className="bg-cream-50 divide-y divide-espresso-100">
                    {sortedByCategory.map((row) => (
                      <tr key={row.category} className="hover:bg-cream-100">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-espresso-800">
                          {CATEGORY_LABELS[row.category] || row.category}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-espresso-800">
                          ${Number(row.total).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
